import {
    Popover,
    PopoverContent,
    PopoverTrigger,
    Button
} from '@nextui-org/react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import 'react-day-picker/style.css'
import { ErrorText } from '@/components/common/UI'
import { Icons } from '@/components/icons'

interface DatePickerControlProps<T extends FieldValues> {
    name: Path<T>
    control: Control<T>
    label: string
    rules?: any
    minDate?: Date
    maxDate?: Date
    availableDates?: Date[]
    classNames?: string
    onCustomChange?: (value: string) => void
    hideLabel?: boolean
}

export function DatePickerControl<T extends FieldValues>({
    name,
    control,
    label,
    rules,
    minDate,
    maxDate,
    availableDates,
    classNames,
    onCustomChange,
    hideLabel
}: DatePickerControlProps<T>): React.ReactElement {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    const isDateAvailable = (date: Date): boolean => {
        if (!availableDates || availableDates.length === 0) {
            return true
        }

        return availableDates.some(
            (availableDate) =>
                availableDate.toISOString().split('T')[0] ===
                date.toISOString().split('T')[0]
        )
    }

    // Helper function to parse date string without timezone issues
    const parseDateString = (dateString: string): Date | undefined => {
        if (!dateString) {
            return undefined
        }
        // Soporta 'dd/MM/yyyy' y 'yyyy-MM-dd'
        if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/').map(Number)

            return new Date(year, month - 1, day)
        }

        const [year, month, day] = dateString.split('-').map(Number)

        return new Date(year, month - 1, day)
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
                const selectedDate = field.value
                    ? parseDateString(field.value)
                    : undefined

                return (
                    <div className="w-full">
                        {!hideLabel && (
                            <label className="block text-xs text-gray-500 font-medium mb-2">
                                {t(label)}
                            </label>
                        )}

                        <Popover
                            isOpen={isOpen}
                            onOpenChange={setIsOpen}
                        >
                            <PopoverTrigger>
                                <Button
                                    variant="bordered"
                                    className={`justify-start text-left font-normal w-full ${classNames} ${
                                        error ? 'border-danger text-danger' : ''
                                    }`}
                                >
                                    <Icons.Calendar className="mr-2 h-4 w-4 text-primary" />
                                    {selectedDate ? (
                                        format(selectedDate, 'PPP', {
                                            locale: es
                                        })
                                    ) : (
                                        <span className="text-muted-foreground">
                                            {t(label)}
                                        </span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 h-80">
                                <DayPicker
                                    mode="single"
                                    hideNavigation
                                    showOutsideDays
                                    selected={selectedDate}
                                    onSelect={(date) => {
                                        if (date) {
                                            const formattedDate = format(
                                                date,
                                                'dd/MM/yyyy'
                                            )

                                            field.onChange(formattedDate)
                                            onCustomChange?.(formattedDate)
                                        }
                                        setIsOpen(false)
                                    }}
                                    disabled={(date) => {
                                        if (minDate && date < minDate) {
                                            return true
                                        }
                                        if (maxDate && date > maxDate) {
                                            return true
                                        }

                                        return !isDateAvailable(date)
                                    }}
                                    locale={es}
                                    captionLayout="dropdown"
                                    fromYear={1900}
                                    toYear={new Date().getFullYear()}
                                    classNames={{
                                        dropdowns: 'pl-8 space-x-14 mt-1',
                                        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',

                                        // Calendar caption (header with month/year)
                                        caption:
                                            'flex justify-center pt-1 relative items-center',
                                        caption_label: 'text-sm font-medium',
                                        // Calendar navigation
                                        nav: 'space-x-1 flex items-center',
                                        nav_button:
                                            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                                        nav_button_previous: 'absolute left-1',
                                        nav_button_next: 'absolute right-1',
                                        // Calendar table
                                        table: 'w-full border-collapse space-y-1',
                                        head_row: 'flex',
                                        head_cell:
                                            'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
                                        // Calendar rows
                                        row: 'flex w-full mt-2',
                                        cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
                                        // Calendar day buttons
                                        day: 'h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground',
                                        day_selected:
                                            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                                        day_today:
                                            'bg-accent text-accent-foreground',
                                        day_outside:
                                            'text-muted-foreground opacity-50',
                                        day_disabled:
                                            'text-muted-foreground opacity-50',
                                        day_range_middle:
                                            'aria-selected:bg-accent aria-selected:text-accent-foreground',
                                        day_hidden: 'invisible'
                                    }}
                                />
                            </PopoverContent>
                        </Popover>

                        {error && <ErrorText error={error?.message} />}
                    </div>
                )
            }}
        />
    )
}
